import React, { ComponentType } from 'react';
import FaceBookIcon from 'svg/FaceBookIcon';
import Globe from 'svg/Globe';
import InstagramIcon from 'svg/InstagramIcon';
import LinkedInIcon from 'svg/LinkedInIcon';
import Mail from 'svg/Mail';
import Phone from 'svg/Phone';
import XIcon from 'svg/XIcon';
import YoutubeIcon from 'svg/YoutubeIcon';

interface Props {
  websiteUrl?: string;
  email?: string;
  phoneNumber?: string;
  instagramUrl?: string | null;
  facebookUrl?: string | null;
  youtubeUrl?: string | null;
  linkedinUrl?: string | null;
  twitterUrl?: string | null;
  /** Optional action (e.g. "Contact club" button) rendered to the right of the social icons. */
  cta?: React.ReactNode;
}

const normalizeUrl = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

interface SocialLink {
  key: string;
  url: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}

export default function ContactCard({
  websiteUrl,
  email,
  phoneNumber,
  instagramUrl,
  facebookUrl,
  youtubeUrl,
  linkedinUrl,
  twitterUrl,
  cta,
}: Props) {
  const socialLinks: SocialLink[] = [
    { key: 'website', url: websiteUrl || '', label: 'Visit club website', Icon: Globe },
    { key: 'instagram', url: instagramUrl || '', label: 'Instagram', Icon: InstagramIcon },
    { key: 'facebook', url: facebookUrl || '', label: 'Facebook', Icon: FaceBookIcon },
    { key: 'youtube', url: youtubeUrl || '', label: 'YouTube', Icon: YoutubeIcon },
    { key: 'linkedin', url: linkedinUrl || '', label: 'LinkedIn', Icon: LinkedInIcon },
    { key: 'twitter', url: twitterUrl || '', label: 'X', Icon: XIcon },
  ].filter((link) => !!link.url);

  const hasContent = socialLinks.length > 0 || !!email || !!phoneNumber || !!cta;

  if (!hasContent) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-[1.25rem] bg-color-bg-lightmode-tertiary px-6 py-4 dark:bg-color-bg-darkmode-tertiary">
      {(socialLinks.length > 0 || !!cta) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {socialLinks.map(({ key, url, label, Icon }) => (
              <a
                key={key}
                href={normalizeUrl(url)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-color-text-lightmode-primary text-color-text-lightmode-primary transition-colors hover:bg-color-bg-lightmode-primary dark:border-color-text-darkmode-primary dark:text-color-text-darkmode-primary"
              >
                <Icon className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
          {cta}
        </div>
      )}

      {email && (
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          <a
            href={`mailto:${email}`}
            className="typography-product-body break-all text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
          >
            {email}
          </a>
        </div>
      )}

      {phoneNumber && (
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          <a
            href={`tel:${phoneNumber}`}
            className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
          >
            {phoneNumber}
          </a>
        </div>
      )}
    </div>
  );
}
